/**
 * LifecycleBanner — surfaces transient billing states to the user in /my-plan.
 *
 * States rendered (one banner at a time, in priority order):
 *
 *  1. past_due              → RED   · "Tu último pago falló" + countdown + CTA
 *  2. pending_proration     → AMBER · Upgrade aún sin pagar + countdown + CTA
 *  3. pending_cancellation  → BLUE  · "Tu suscripción terminará el [date]"   + Revertir
 *  4. pending_downgrade     → GRAY  · "Bajarás a [plan] el [date]"           + Revertir
 *  5. paused                → SLATE · "MP pausó tu suscripción"
 *
 * Always reversible from inside the banner. The mutations call the new
 * iter-168 endpoints (cancel-pending-cancellation / cancel-pending-downgrade /
 * reactivate).
 */
import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AlertTriangle, XCircle, Clock, ArrowDownCircle, Pause, ExternalLink, Undo2, Heart,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { billingService } from "../../services/billingService";

function _countdown(iso) {
  if (!iso) return null;
  try {
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return { expired: true, label: "expirado" };
    const days = Math.floor(ms / (1000 * 60 * 60 * 24));
    const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (days >= 1) return { expired: false, label: `${days}d ${hours}h` };
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    return { expired: false, label: `${hours}h ${minutes}m` };
  } catch { return null; }
}

function _fmtDate(iso, lang) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(lang === "en" ? "en-US" : "es-AR", {
      day: "2-digit", month: "long", year: "numeric",
    });
  } catch { return iso; }
}

export function LifecycleBanner({ comp, lang = "es" }) {
  const qc = useQueryClient();
  const isEs = lang !== "en";

  const revertCancel = useMutation({
    mutationFn: () => billingService.compositionCancelPendingCancellation(),
    onSuccess: (data) => {
      if (data?.requires_reauthorize) {
        toast.warning(isEs ? data.message : "Reauthorise required.");
      } else {
        toast.success(isEs ? "¡Tu suscripción sigue activa!" : "Subscription kept active!");
      }
      qc.invalidateQueries({ queryKey: ["billing-me"] });
    },
    onError: () => toast.error(isEs ? "No pudimos revertir. Reintenta." : "Could not revert. Retry."),
  });

  const revertDowngrade = useMutation({
    mutationFn: () => billingService.compositionCancelPendingDowngrade(),
    onSuccess: () => {
      toast.success(isEs ? "Mantendrás tu plan actual." : "Keeping your current plan.");
      qc.invalidateQueries({ queryKey: ["billing-me"] });
    },
    onError: () => toast.error(isEs ? "No pudimos revertir. Reintenta." : "Could not revert. Retry."),
  });

  const reactivate = useMutation({
    mutationFn: () => billingService.compositionReactivate(),
    onSuccess: (data) => {
      if (data?.mp_dashboard_url) window.open(data.mp_dashboard_url, "_blank", "noopener,noreferrer");
    },
    onError: () => toast.error(isEs ? "No pudimos abrir la guía." : "Could not open guide."),
  });

  // iter-168d: win-back offer (only fetched when comp is in pending_cancellation
  // since the eligibility check is the same for any active state).
  const { data: winback } = useQuery({
    queryKey: ["winback-eligibility", comp?.id, comp?.status],
    queryFn: () => billingService.winbackEligibility(),
    enabled: comp?.status === "pending_cancellation",
    staleTime: 60_000,
  });

  const claimWinback = useMutation({
    mutationFn: () => billingService.winbackClaim(),
    onSuccess: (data) => {
      toast.success(
        isEs
          ? `¡Gracias por quedarte! Aplicamos ${data?.discount_pct}% off en tu próxima renovación.`
          : `Thanks for staying! ${data?.discount_pct}% off applied to your next renewal.`,
        { duration: 6000 },
      );
      qc.invalidateQueries({ queryKey: ["billing-me"] });
      qc.invalidateQueries({ queryKey: ["winback-eligibility"] });
    },
    onError: (e) => {
      const detail = e?.response?.data?.detail || "";
      toast.error(isEs ? `No pudimos aplicar la oferta. ${detail}` : `Could not apply offer. ${detail}`);
    },
  });

  const banner = useMemo(() => {
    if (!comp) return null;
    const status = comp.status;
    if (status === "past_due") {
      const cd = _countdown(comp.grace_period_ends_at);
      return { kind: "past_due", countdown: cd };
    }
    // proration pending (upgrade aún sin pagar)
    if (comp.pending_proration_status === "pending" && comp.pending_proration_expires_at) {
      const cd = _countdown(comp.pending_proration_expires_at);
      return { kind: "pending_proration", countdown: cd };
    }
    if (status === "pending_cancellation") return { kind: "pending_cancellation" };
    if (status === "pending_downgrade") return { kind: "pending_downgrade" };
    if (status === "paused") return { kind: "paused" };
    return null;
  }, [comp]);

  if (!banner) return null;

  if (banner.kind === "past_due") {
    return (
      <div
        data-testid="lifecycle-banner-past-due"
        className="rounded-2xl border border-rose-200 bg-rose-50/60 p-5 flex items-start gap-4"
      >
        <div className="shrink-0 h-10 w-10 rounded-full bg-rose-100 grid place-items-center">
          <AlertTriangle className="h-5 w-5 text-rose-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-rose-900">
            {isEs ? "Tu último pago no se pudo procesar" : "Your last payment failed"}
          </h3>
          <p className="mt-1 text-sm text-rose-800/80">
            {isEs
              ? `Tienes ${banner.countdown?.label || "unos días"} para actualizar tu método de pago. Después, tu plan se suspende automáticamente.`
              : `You have ${banner.countdown?.label || "a few days"} to update your payment method. After that, your plan auto-suspends.`}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              data-testid="lifecycle-banner-past-due-cta"
              size="sm"
              onClick={() => reactivate.mutate()}
              disabled={reactivate.isPending}
              className="bg-rose-600 hover:bg-rose-700 text-white gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              {isEs ? "Actualizar método de pago" : "Update payment method"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (banner.kind === "pending_proration") {
    return (
      <div
        data-testid="lifecycle-banner-proration"
        className="rounded-2xl border border-amber-200 bg-amber-50/60 p-5 flex items-start gap-4"
      >
        <div className="shrink-0 h-10 w-10 rounded-full bg-amber-100 grid place-items-center">
          <Clock className="h-5 w-5 text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-amber-900">
            {isEs ? "Falta confirmar tu upgrade" : "Upgrade payment pending"}
          </h3>
          <p className="mt-1 text-sm text-amber-800/80">
            {isEs
              ? `Cobramos ${comp.pending_proration_amount?.toLocaleString("es-CO") || ""} por el ajuste de plan. Tienes ${banner.countdown?.label || "3 días"} para completar el pago, después volvemos a tu plan anterior.`
              : `We've charged ${comp.pending_proration_amount?.toLocaleString("en-US") || ""} for the plan upgrade. You have ${banner.countdown?.label || "3 days"} to pay; otherwise we'll restore your previous plan.`}
          </p>
        </div>
      </div>
    );
  }

  if (banner.kind === "pending_cancellation") {
    const hasWinback = winback?.eligible;
    return (
      <div
        data-testid="lifecycle-banner-pending-cancel"
        className="rounded-2xl border border-sky-200 bg-sky-50/60 p-5 flex items-start gap-4 transition-all duration-500 ease-out"
      >
        <div className="shrink-0 h-10 w-10 rounded-full bg-sky-100 grid place-items-center">
          <XCircle className="h-5 w-5 text-sky-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-sky-900">
            {isEs ? "Tu suscripción terminará pronto" : "Your subscription is ending"}
          </h3>
          <p className="mt-1 text-sm text-sky-800/80">
            {isEs
              ? `Mantienes acceso hasta el ${_fmtDate(comp.cancellation_effective_at, lang)}. Después tu plan baja a gratuito automáticamente.`
              : `You'll keep access until ${_fmtDate(comp.cancellation_effective_at, lang)}. After that, your plan drops to free automatically.`}
          </p>

          {/* iter-168d: Win-back inline offer (configurable from admin). */}
          {hasWinback ? (
            <div
              data-testid="lifecycle-winback-offer"
              className="mt-4 rounded-xl border border-rose-200 bg-gradient-to-br from-rose-50 to-amber-50 p-4 flex items-start gap-3 animate-in fade-in slide-in-from-top-2 duration-500"
            >
              <Heart className="h-5 w-5 text-rose-500 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-rose-900 leading-relaxed">{winback.copy}</p>
                <div className="mt-3">
                  <Button
                    data-testid="lifecycle-winback-claim"
                    size="sm"
                    onClick={() => claimWinback.mutate()}
                    disabled={claimWinback.isPending}
                    className="bg-rose-600 hover:bg-rose-700 text-white gap-2"
                  >
                    <Heart className="h-4 w-4" />
                    {isEs
                      ? `Quedarme con ${winback.discount_pct}% off`
                      : `Stay with ${winback.discount_pct}% off`}
                  </Button>
                </div>
              </div>
            </div>
          ) : null}

          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              data-testid="lifecycle-banner-revert-cancel"
              size="sm"
              variant="outline"
              onClick={() => revertCancel.mutate()}
              disabled={revertCancel.isPending}
              className="gap-2 border-sky-300 text-sky-900 hover:bg-sky-100"
            >
              <Undo2 className="h-4 w-4" />
              {isEs ? "Cancelar la cancelación" : "Undo cancellation"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (banner.kind === "pending_downgrade") {
    return (
      <div
        data-testid="lifecycle-banner-pending-downgrade"
        className="rounded-2xl border border-slate-300 bg-slate-50/80 p-5 flex items-start gap-4"
      >
        <div className="shrink-0 h-10 w-10 rounded-full bg-slate-200 grid place-items-center">
          <ArrowDownCircle className="h-5 w-5 text-slate-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-slate-900">
            {isEs ? "Cambio de plan programado" : "Plan change scheduled"}
          </h3>
          <p className="mt-1 text-sm text-slate-700">
            {isEs
              ? `Tu plan bajará el ${_fmtDate(comp.downgrade_effective_at, lang)}. Hasta entonces, conservás tu tier actual.`
              : `Your plan will change on ${_fmtDate(comp.downgrade_effective_at, lang)}. Until then, you keep your current tier.`}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              data-testid="lifecycle-banner-revert-downgrade"
              size="sm"
              variant="outline"
              onClick={() => revertDowngrade.mutate()}
              disabled={revertDowngrade.isPending}
              className="gap-2"
            >
              <Undo2 className="h-4 w-4" />
              {isEs ? "Mantener plan actual" : "Keep current plan"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (banner.kind === "paused") {
    return (
      <div
        data-testid="lifecycle-banner-paused"
        className="rounded-2xl border border-slate-300 bg-slate-100/80 p-5 flex items-start gap-4"
      >
        <div className="shrink-0 h-10 w-10 rounded-full bg-slate-200 grid place-items-center">
          <Pause className="h-5 w-5 text-slate-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-slate-900">
            {isEs ? "Suscripción en pausa" : "Subscription paused"}
          </h3>
          <p className="mt-1 text-sm text-slate-700">
            {isEs
              ? "MercadoPago pausó tu suscripción tras varios intentos fallidos. Contactanos para reactivarla."
              : "MercadoPago paused your subscription after several failed retries. Contact us to reactivate."}
          </p>
        </div>
      </div>
    );
  }

  return null;
}
